import {
    exerciseDelay,
    exerciseFinished,
    exerciseLeftRight, exerciseRoundLeft, exerciseRoundRight,
    exerciseUpDown,
    exerciseUpLeftDownRight, exerciseUpRightDownLeft,
    exerciseFigure8Horizontal,
    exerciseFigure8Vertical,
    exerciseBlink,
    exerciseSlowCircleLeft,
    exerciseEyeRollComplete,
    exerciseSquareClockwise,
    exerciseSquareCounterClockwise,
    exerciseTriangle,
    exerciseStar,
    exerciseSlowCircleRight,
    exerciseWavePattern,
    calculateExerciseDuration,
    ACTIVITY_TYPE_EXERCISE
} from "../tools/ExerciseUtils";
import { strings } from "../languages/localizationStrings";

const firstSet = (repetitions) => {
    return [exerciseDelay(5, strings.getReady),
    exerciseLeftRight(repetitions),
    exerciseDelay(5, strings.blink),
    exerciseUpDown(repetitions),
    exerciseDelay(5, strings.blink),
    exerciseUpRightDownLeft(repetitions),
    exerciseDelay(5, strings.blink),
    exerciseUpLeftDownRight(repetitions),
    ];
}

const addFinish = (set) => {
    return [...set,
    exerciseFinished(strings.finishedForToday)];
}

// Factory functions that create exercise arrays with current translations.
// These must be called at render time (not module load time) so that
// exercise names reflect the currently selected language.

export const getBeginner = () => addFinish(firstSet(5));

export const getIntermediate = () =>
    addFinish([...firstSet(10),
    exerciseDelay(5, strings.blink),
    exerciseRoundLeft(2),
    exerciseDelay(5, strings.blink),
    exerciseRoundRight(2)
    ]);

export const getAdvanced = () => addFinish([...firstSet(15),
exerciseDelay(5, strings.blink),
exerciseRoundLeft(5),
exerciseDelay(5, strings.blink),
exerciseRoundRight(5)
]);

export const getIntense = () => addFinish([...firstSet(30),
exerciseDelay(20, strings.blink),
exerciseRoundLeft(10),
exerciseDelay(5, strings.blink),
exerciseRoundRight(10)
]);

// ===== PROGRESSIVE LEVELS - Balanced workouts with 6-8 exercises =====

// Progressive Easy: Basics + Figure-8 patterns (6 exercises)
export const getProgressiveEasy = () => addFinish([
exerciseDelay(5, strings.getReady),
exerciseLeftRight(8),
exerciseDelay(5, strings.blink),
exerciseUpDown(8),
exerciseDelay(5, strings.blink),
exerciseFigure8Horizontal(6),
exerciseDelay(5, strings.blink),
exerciseFigure8Vertical(6),
exerciseDelay(5, strings.blink),
exerciseTriangle(5),
exerciseDelay(5, strings.blink),
exerciseSlowCircleLeft(5)
]);

// Progressive Medium: Diagonal tracking + geometric patterns (7 exercises)
export const getProgressiveMedium = () => addFinish([
exerciseDelay(5, strings.getReady),
exerciseLeftRight(10),
exerciseDelay(5, strings.blink),
exerciseUpDown(10),
exerciseDelay(5, strings.blink),
exerciseUpRightDownLeft(10),
exerciseDelay(5, strings.blink),
exerciseUpLeftDownRight(10),
exerciseDelay(5, strings.blink),
exerciseSquareClockwise(8),
exerciseDelay(5, strings.blink),
exerciseStar(6),
exerciseDelay(5, strings.blink),
exerciseRoundLeft(6)
]);

// Progressive Tough: Advanced tracking with spirals (8 exercises)
export const getProgressiveTough = () => addFinish([
exerciseDelay(5, strings.getReady),
exerciseLeftRight(12),
exerciseDelay(5, strings.blink),
exerciseUpDown(12),
exerciseDelay(5, strings.blink),
exerciseFigure8Horizontal(10),
exerciseDelay(5, strings.blink),
exerciseSquareCounterClockwise(10),
exerciseDelay(5, strings.blink),
exerciseWavePattern(10),
exerciseDelay(5, strings.blink),
exerciseStar(8),
exerciseDelay(5, strings.blink),
exerciseRoundRight(8),
exerciseDelay(5, strings.blink),
exerciseEyeRollComplete(5)
]);

// Geometric Patterns: Shape tracking for precision (7 exercises)
export const getGeometricPatterns = () => addFinish([
exerciseDelay(5, strings.getReady),
exerciseTriangle(8),
exerciseDelay(5, strings.blink),
exerciseSquareClockwise(8),
exerciseDelay(5, strings.blink),
exerciseSquareCounterClockwise(8),
exerciseDelay(5, strings.blink),
exerciseStar(8),
exerciseDelay(5, strings.blink),
exerciseFigure8Horizontal(8),
exerciseDelay(5, strings.blink),
exerciseFigure8Vertical(8),
exerciseDelay(5, strings.blink),
exerciseWavePattern(8)
]);

// Static instances for duration/count calculations (language-independent numbers)
const _beginner = getBeginner();
const _intermediate = getIntermediate();
const _advanced = getAdvanced();
const _intense = getIntense();
const _progressiveEasy = getProgressiveEasy();
const _progressiveMedium = getProgressiveMedium();
const _progressiveTough = getProgressiveTough();
const _geometricPatterns = getGeometricPatterns();

// Calculate durations for each exercise set (in minutes)
export const exerciseDurations = {
    // Basics Group
    beginner: calculateExerciseDuration(_beginner),
    intermediate: calculateExerciseDuration(_intermediate),
    advanced: calculateExerciseDuration(_advanced),
    intense: calculateExerciseDuration(_intense),
    // Progressive Group
    progressiveEasy: calculateExerciseDuration(_progressiveEasy),
    progressiveMedium: calculateExerciseDuration(_progressiveMedium),
    progressiveTough: calculateExerciseDuration(_progressiveTough),
    geometricPatterns: calculateExerciseDuration(_geometricPatterns),
};

// Get exercise counts for each set (active exercises, not delays)
export const exerciseCounts = {
    // Basics Group
    beginner: _beginner.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    intermediate: _intermediate.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    advanced: _advanced.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    intense: _intense.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    // Progressive Group
    progressiveEasy: _progressiveEasy.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    progressiveMedium: _progressiveMedium.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    progressiveTough: _progressiveTough.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
    geometricPatterns: _geometricPatterns.filter(e => e.type === ACTIVITY_TYPE_EXERCISE).length,
};

// Exercise groups factory - must be called at render time to get current translations
export const getExerciseGroups = () => ({
    basics: {
        name: 'basics',
        displayName: strings.groupBasics,
        description: strings.groupBasicsDesc,
        levels: [
            { key: 'beginner', name: strings.easy },
            { key: 'intermediate', name: strings.medium },
            { key: 'advanced', name: strings.tough },
            { key: 'intense', name: strings.intense }
        ]
    },
    progressive: {
        name: 'progressive',
        displayName: strings.groupProgressive,
        description: strings.groupProgressiveDesc,
        levels: [
            { key: 'progressiveEasy', name: strings.progressiveEasy },
            { key: 'progressiveMedium', name: strings.progressiveMedium },
            { key: 'progressiveTough', name: strings.progressiveTough },
            { key: 'geometricPatterns', name: strings.geometricPatterns }
        ]
    }
});
